import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import XtermTerminal from "./XtermTerminal";
import { buildTelnetWsUrl, buildTokenSubprotocols, getToken } from "../../utils/consoleUrl";

const RECONNECT_DELAYS = [2500, 5000, 10000, 20000]; // backoff exponentiel
const MAX_RETRIES = 4;

function closeCodeLabel(code) {
  if (code === 1000) return "fermeture normale";
  if (code === 1001) return "page fermée";
  if (code === 1006) return "connexion TCP refusée — GNS3 injoignable ou nœud arrêté";
  if (code === 1011) return "erreur serveur proxy";
  if (code === 4401) return "token invalide — reconnectez-vous";
  return `code ${code}`;
}

export default function TelnetConsolePanel({ host, port, onStatusChange }) {
  const [status, setStatus]     = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const wsRef            = useRef(null);
  const termApiRef       = useRef(null);
  const mountedRef       = useRef(false);
  const reconnectTimer   = useRef(null);
  const retryCount       = useRef(0);
  const statusRef        = useRef("idle");

  const token = useMemo(() => getToken(), []);
  const wsUrl = useMemo(() => {
    if (!host || !port || !token) return "";
    // SEC-WS-01 : token transmis via Sec-WebSocket-Protocol (buildTokenSubprotocols
    // au point de creation du WebSocket ci-dessous), plus jamais dans l'URL.
    return buildTelnetWsUrl({ host, port });
  }, [host, port, token]);

  const updateStatus = useCallback((s, err = "") => {
    statusRef.current = s;
    setStatus(s);
    setErrorMsg(err);
    onStatusChange?.(s, err);
  }, [onStatusChange]);

  const closeWs = useCallback(() => {
    clearTimeout(reconnectTimer.current);
    reconnectTimer.current = null;
    const ws = wsRef.current;
    if (!ws) return;
    wsRef.current = null;
    if (ws.readyState === WebSocket.CONNECTING) return;
    try { ws.close(); } catch {}
  }, []);

  const connect = useCallback(() => {
    const termApi = termApiRef.current;
    const term    = termApi?.term;
    if (!term || !wsUrl) {
      updateStatus("error", "URL WebSocket manquante");
      return;
    }

    closeWs();
    updateStatus("connecting");
    term.writeln("\r\n\x1b[33m[CONNECTING...]\x1b[0m");

    const ws = new WebSocket(wsUrl, buildTokenSubprotocols(token));
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    ws.onopen = () => {
      if (!mountedRef.current) return;
      retryCount.current = 0;
      updateStatus("connected");
      setErrorMsg("");
      termApi?.safeFit?.();
    };

    ws.onmessage = (evt) => {
      if (!mountedRef.current) return;
      if (evt.data instanceof ArrayBuffer) {
        const bytes = new Uint8Array(evt.data);
        let s = "";
        const chunk = 0x8000;
        for (let i = 0; i < bytes.length; i += chunk)
          s += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
        term.write(s);
      } else if (typeof evt.data === "string") {
        term.write(evt.data);
      }
    };

    ws.onerror = () => {
      // onerror est toujours suivi de onclose — on laisse onclose gérer
    };

    ws.onclose = (evt) => {
      if (!mountedRef.current) return;

      const label = closeCodeLabel(evt.code);
      const isClean   = evt.code === 1000 || evt.code === 1001;
      const isAuthErr = evt.code === 4401;

      if (isClean) {
        updateStatus("closed", "");
        term.writeln(`\r\n\x1b[90m[CLOSED] ${label}\x1b[0m`);
        return;
      }

      if (isAuthErr) {
        updateStatus("error", "Token invalide — rechargez la page.");
        term.writeln(`\r\n\x1b[31m[AUTH ERROR] Token invalide ou expiré.\x1b[0m`);
        return;
      }

      // Erreur réseau / TCP — reconnexion avec backoff
      const retry = retryCount.current;
      const delay = RECONNECT_DELAYS[Math.min(retry, RECONNECT_DELAYS.length - 1)];
      const delayS = (delay / 1000).toFixed(0);

      term.writeln(`\r\n\x1b[31m[CLOSED]\x1b[0m ${label}`);

      if (retry < MAX_RETRIES) {
        term.writeln(`\x1b[33m[INFO]\x1b[0m reconnecting in ${delayS}s… (tentative ${retry + 1}/${MAX_RETRIES})`);
        updateStatus("connecting", `Reconnexion ${retry + 1}/${MAX_RETRIES} dans ${delayS}s…`);
        retryCount.current += 1;
        reconnectTimer.current = setTimeout(() => {
          if (mountedRef.current && statusRef.current !== "connected") connect();
        }, delay);
      } else {
        updateStatus("error", `Connexion échouée (${label}). Vérifiez que GNS3 est démarré et que le nœud est actif.`);
        term.writeln(`\r\n\x1b[31m[FAILED]\x1b[0m Trop de tentatives. Cliquez sur Reconnect.`);
        retryCount.current = 0;
      }
    };

    const disp = term.onData((chunk) => {
      try {
        if (ws.readyState === WebSocket.OPEN) ws.send(chunk);
      } catch {}
    });
    ws.addEventListener("close", () => disp?.dispose?.());

  }, [wsUrl, closeWs, updateStatus]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      closeWs();
    };
  }, [closeWs]);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center"
        justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Telnet {host}:{port} —{" "}
          <b style={{ color:
            status === "connected" ? "#4caf50" :
            status === "error"     ? "#f44336" :
            status === "connecting"? "#ff9800" : "inherit"
          }}>{status}</b>
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => { retryCount.current = 0; connect(); }}
          disabled={status === "connecting"}
        >
          Reconnect
        </Button>
      </Stack>

      {errorMsg && (
        <Alert severity={status === "error" ? "error" : "info"}
          sx={{ mb: 1 }} onClose={() => setErrorMsg("")}>
          {errorMsg}
        </Alert>
      )}

      <XtermTerminal
        cacheKey={`${host}:${port}`}
        onReady={(api) => {
          termApiRef.current = api;
          api.term.writeln("\x1b[90mTerminal prêt.\x1b[0m Connexion en cours…");
          connect();
        }}
      />
    </Box>
  );
}
